import type { MessageFeedback } from 'api/ai-builder/requests';

import { AssistantCompactionMessage } from './assistant-compaction-message';
import { AssistantMaxRoundsMessage } from './assistant-max-rounds-message';
import { AssistantMessage } from './assistant-message';
import { ThinkingMessage } from './thinking-message';
import { ToolMessage, type ToolActionProps } from './tool-message';
import { UserFindingMessage } from './user-finding-message';
import { UserMessage } from './user-message';

import type { ChatMessage } from '../../hooks';

type MessageItemProps = ToolActionProps & {
  message: ChatMessage;
  isNextTool: boolean;
  isDescriptionOpen: boolean;
  isStreaming?: boolean;
  onToggleDescription: () => void;
  onFeedback?: (messageId: string, feedback: MessageFeedback | null) => void;
  onSubmitDislikeFeedback?: (messageId: string, reasons: string[], details: string) => void;
};

export const MessageItem = ({
  message,
  isNextTool,
  isDescriptionOpen,
  isStreaming,
  onToggleDescription,
  onFeedback,
  onSubmitDislikeFeedback,
  ...toolActions
}: MessageItemProps) => {
  const renderContent = () => {
    if (message.role === 'user') {
      if (message.data?.type === 'review_finding') {
        return <UserFindingMessage finding={message.data.finding} />;
      }
      return <UserMessage id={message.id} text={message.text} files={message.files} />;
    }

    if (message.role === 'tool') {
      return (
        <ToolMessage
          {...toolActions}
          toolName={message.toolName}
          toolLabel={message.toolLabel}
          toolDescription={message.toolDescription}
          status={message.status}
          data={message.data}
          errorMessage={message.errorMessage}
          isNextTool={isNextTool}
          isDescriptionOpen={isDescriptionOpen}
          onToggleDescription={onToggleDescription}
        />
      );
    }

    switch (message.messageType) {
      case 'thinking':
        return <ThinkingMessage text={message.text} timeTakenSeconds={message.timeTakenSeconds} />;
      case 'compaction':
        return <AssistantCompactionMessage text={message.text} />;
      default:
        return message.errorType === 'max_rounds_exceeded' ? (
          <AssistantMaxRoundsMessage text={message.text} />
        ) : (
          <AssistantMessage
            text={message.text}
            errorMessage={message.errorMessage}
            feedbackRating={message.feedbackRating}
            onFeedback={
              onFeedback ? (feedbackRating) => onFeedback(message.id, feedbackRating) : undefined
            }
            onSubmitDislikeFeedback={
              onSubmitDislikeFeedback
                ? (reasons, details) => onSubmitDislikeFeedback(message.id, reasons, details)
                : undefined
            }
            isStreaming={isStreaming}
          />
        );
    }
  };

  return <div data-message-id={message.id}>{renderContent()}</div>;
};
